import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { CardHeaderWithBadges } from '@/components/ui/CardHeader';
import { getServiceStatusBadge, getHealthBadge } from '@/utils/badgeUtils';
import { MSAService } from '@/types';
import { CATEGORY_LABELS, ServiceCategory, getServiceIcon } from '@/constants/serviceCategories';
import { HealthStatus } from '@/types/health';

interface ServiceCardProps {
  service: MSAService;
  healthStatuses: Record<number, HealthStatus>;
  isSubscribed?: boolean;
  showStatusBadge?: boolean; // 상태 뱃지 표시 여부 (기본: true)
  onServiceClick: (service: MSAService, event?: React.MouseEvent) => void;
  onSubscribe?: (serviceSlug: string) => void;
  onUnsubscribe?: (serviceSlug: string) => void;
  onConnect?: (service: MSAService) => void;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  healthStatuses,
  isSubscribed,
  showStatusBadge = true,
  onServiceClick,
  onSubscribe: _onSubscribe,
  onUnsubscribe: _onUnsubscribe,
  onConnect,
}) => {
  const isDeleted = service.status === 'deleted';
  const isSuspended = service.status === 'suspended';
  const isTrial = service.metadata?.subscription?.is_trial ?? false;

  return (
    <Card 
      className={`transition-shadow ${isDeleted ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-lg'}`}
      onClick={(e) => {
        if (isDeleted) {
          alert('이 서비스는 삭제되었습니다.');
          return;
        }
        onServiceClick(service, e);
      }}
    >
      <CardHeader>
        <CardHeaderWithBadges
          icon={getServiceIcon(service.metadata, service.category)}
          title={service.service_name}
          subtitle={CATEGORY_LABELS[service.category as ServiceCategory] || service.category}
          version={service.version}
          muted={isDeleted}
          badges={[
            showStatusBadge && getServiceStatusBadge(service.status),
            service.status === 'approved' && getHealthBadge(healthStatuses[service.id]),
            isTrial && <Badge variant="warning" size="sm">체험판</Badge>,
          ].filter(Boolean) as React.ReactNode[]}
        />
      </CardHeader>
      <CardContent>
        {service.service_description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-2">
            {service.service_description}
          </p>
        )}
        {service.contact_name && (
          <p className="text-xs text-gray-500 mb-4">
            개발사: {service.contact_name}
          </p>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 break-all flex-1 min-w-0 mr-2 transition-colors duration-300 hover:text-blue-600 cursor-pointer">
            {service.service_url}
          </span>
          {isSubscribed !== undefined ? (
            // 검색 결과용 (구독중 뱃지)
            <div className="flex items-center gap-2">
              {isSubscribed && (
                <Badge variant="info" size="sm">
                  구독중
                </Badge>
              )}
            </div>
          ) : (
            // 구독 서비스용 (접속 버튼만)
            <Button 
              size="sm" 
              disabled={isDeleted || healthStatuses[service.id] !== 'ONLINE'}
              variant={isSuspended || healthStatuses[service.id] !== 'ONLINE' ? 'outline' : 'default'}
              onClick={(e) => {
                e.stopPropagation();
                if (onConnect && healthStatuses[service.id] === 'ONLINE' && !isSuspended) {
                  onConnect(service);
                } else {
                  onServiceClick(service, e);
                }
              }}
            >
              {isSuspended ? '상태 확인' : healthStatuses[service.id] === 'ONLINE' ? '접속' : '상태 확인'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

